/**
 * Fandom header behaviour (header setting "style: fandom"). Loaded as a module only in that style.
 *
 * <fd-mega-nav> wraps Horizon's <nav header-menu>. It opens the panel of a top-level item on hover (after a short
 * intent delay), on click and on Enter/Space (the item is a <button>), and closes it when the pointer leaves the item
 * and its panel, on Escape (focus returns to the item when it was inside), when focus leaves the item, when the
 * search or region button is hovered, when the dimmed page is clicked and when the header scrolls away.
 * Items in the default slot of Horizon's overflow list are handled here; items Horizon moved into "More" keep
 * Horizon's own activation through the public `activate()` / `deactivate()` of <header-menu> (pointer and focus are
 * forwarded, as the `on:*` attributes of Horizon's own items did). Horizon's `deactivate()` waits while the pointer is
 * anywhere over <overflow-list>, Fandom items included, so "More" is hidden (`data-fd-more-off`) while a Fandom item
 * is hovered, focused or open, and released again once the pointer or focus has left the list.
 *
 * Openers coordinate through document events: `fd:header-open` (detail.source = the opener; every other opener
 * closes) and `fd:header-close` (everything closes). The dim overlay `.fd-hd-dim` is shared and created on demand.
 */

const INTENT_DELAY = 150;
const LEAVE_DELAY = 250;
const OPEN_EVENT = 'fd:header-open';
const CLOSE_EVENT = 'fd:header-close';

/** Dim overlay below the header bar, shared by every opener. */
const dim = {
  /** @type {Set<HTMLElement>} */
  owners: new Set(),
  /** @type {HTMLElement | null} */
  el: null,
  /** @type {MutationObserver | null} */
  observer: null,

  /** @param {HTMLElement} owner */
  show(owner) {
    const root = owner.closest('.fd-hd');
    if (!(root instanceof HTMLElement)) return;
    if (!this.el || this.el.parentElement !== root) {
      this.el?.remove();
      this.observer?.disconnect();
      this.el = document.createElement('div');
      this.el.className = 'fd-hd-dim';
      this.el.setAttribute('aria-hidden', 'true');
      root.append(this.el);
      // Scrolling down, Horizon's sticky header fades out (data-animating) and then leaves the viewport
      // (data-sticky-state="idle") after the scroll event: check again when those change.
      this.observer = new MutationObserver(() => {
        if (dim.el && !dim.el.hidden) dim.place();
      });
      this.observer.observe(root, { attributes: true, attributeFilter: ['data-animating', 'data-sticky-state'] });
    }
    this.owners.add(owner);
    this.el.hidden = false;
    window.addEventListener('scroll', dim.place, { passive: true });
    window.addEventListener('resize', dim.place);
    dim.place();
  },

  /** @param {HTMLElement} owner */
  hide(owner) {
    this.owners.delete(owner);
    if (this.owners.size > 0) return;
    if (this.el) this.el.hidden = true;
    window.removeEventListener('scroll', dim.place);
    window.removeEventListener('resize', dim.place);
  },

  /** Keeps the overlay just below the bar; closes everything once the header hides or has scrolled away. */
  place() {
    const el = dim.el;
    const root = el?.parentElement;
    if (!el || !root) return;
    if (root.hasAttribute('data-animating') || root.getAttribute('data-sticky-state') === 'idle') {
      document.dispatchEvent(new CustomEvent(CLOSE_EVENT));
      return;
    }
    let bottom = 0;
    for (const row of root.querySelectorAll('.header__row')) {
      const rect = row.getBoundingClientRect();
      if (rect.height > 0) bottom = Math.max(bottom, rect.bottom);
    }
    if (bottom === 0) bottom = root.getBoundingClientRect().bottom;
    if (bottom <= 0) {
      document.dispatchEvent(new CustomEvent(CLOSE_EVENT));
      return;
    }
    el.style.setProperty('--fd-dim-top', `${Math.round(bottom)}px`);
  },
};

class FdMegaNav extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLElement | null} */
  #open = null;
  #intentTimer = 0;
  #leaveTimer = 0;
  /** True while Escape moves focus from an item in "More" back to the "More" button. */
  #escaping = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const item of this.querySelectorAll('[data-fd-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      item.addEventListener('pointerenter', (event) => this.#onEnter(event, item), { signal });
      item.addEventListener('pointerleave', (event) => this.#onLeave(event, item), { signal });
    }
    this.querySelector('[slot="more"]')?.addEventListener(
      'pointerenter',
      () => {
        this.close();
        this.#hideMore(false);
      },
      { signal }
    );
    this.querySelector('overflow-list')?.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'mouse') this.#releaseMore();
      },
      { signal }
    );

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener(CLOSE_EVENT, () => this.close(), { signal });
    document.addEventListener(
      OPEN_EVENT,
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.source !== this) this.close();
      },
      { signal }
    );
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    window.addEventListener('resize', () => this.close(), { signal });

    const root = this.closest('.fd-hd');
    for (const el of root ? root.querySelectorAll('.search-action, fd-region-drop') : []) {
      el.addEventListener('pointerenter', () => this.close(), { signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.close();
  }

  /** @param {HTMLElement} item */
  open(item) {
    clearTimeout(this.#intentTimer);
    clearTimeout(this.#leaveTimer);
    if (this.#open === item) return;
    if (!item.querySelector('[data-fd-toggle]')) {
      this.close();
      return;
    }
    this.#leaveMore();
    if (this.#open) {
      this.#setExpanded(this.#open, false);
    } else {
      document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source: this } }));
    }
    this.#setExpanded(item, true);
    this.#open = item;
    dim.show(this);
  }

  /** @param {boolean} [restoreFocus] */
  close(restoreFocus = false) {
    clearTimeout(this.#intentTimer);
    clearTimeout(this.#leaveTimer);
    const item = this.#open;
    if (!item) return;
    this.#open = null;
    this.#setExpanded(item, false);
    dim.hide(this);
    if (restoreFocus) {
      const toggle = item.querySelector('[data-fd-toggle]');
      if (toggle instanceof HTMLElement) toggle.focus();
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    item.querySelector('[data-fd-toggle]')?.setAttribute('aria-expanded', String(expanded));
    item.toggleAttribute('data-fd-open', expanded);
  }

  /** Horizon's <header-menu> (for items that overflow into "More"). */
  get #menu() {
    return /** @type {any} */ (this.closest('header-menu'));
  }

  /** The item Horizon currently shows expanded in "More", if any. */
  get #moreItem() {
    const menuitem = this.querySelector('[slot="overflow"] > [ref="menuitem"][aria-expanded="true"]');
    return menuitem instanceof HTMLElement ? menuitem.parentElement : null;
  }

  /** @param {boolean} hidden - Hide "More" (see snippets/fd-mega-menu.liquid). */
  #hideMore(hidden) {
    this.toggleAttribute('data-fd-more-off', hidden);
  }

  /** Asks Horizon to close "More"; its debounced deactivate completes once the pointer is off the list. */
  #releaseMore() {
    const item = this.#moreItem;
    if (item) this.#menu?.deactivate?.({ target: item });
  }

  /** A Fandom item takes over from "More" (Horizon's own items switched its active item for this). */
  #leaveMore() {
    if (!this.#moreItem) return;
    this.#hideMore(true);
    this.#releaseMore();
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} item
   */
  #onEnter(event, item) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#leaveTimer);
    clearTimeout(this.#intentTimer);
    if (item.slot === 'overflow') {
      this.#hideMore(false);
      this.#menu?.activate?.({ target: item });
      return;
    }
    this.#leaveMore();
    const delay = this.#open ? 0 : INTENT_DELAY;
    this.#intentTimer = window.setTimeout(() => this.open(item), delay);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} item
   */
  #onLeave(event, item) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#intentTimer);
    if (item.slot === 'overflow') {
      this.#menu?.deactivate?.({ target: item });
      return;
    }
    if (this.#open !== item) return;
    clearTimeout(this.#leaveTimer);
    this.#leaveTimer = window.setTimeout(() => this.close(), LEAVE_DELAY);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('[data-fd-toggle]') : null;
    if (!toggle || !this.contains(toggle)) return;
    const item = toggle.closest('[data-fd-item]');
    if (!(item instanceof HTMLElement)) return;
    event.preventDefault();
    clearTimeout(this.#intentTimer);
    if (item.slot === 'overflow') {
      this.#hideMore(false);
      this.#menu?.activate?.({ target: item });
      return;
    }
    if (this.#open === item) {
      // A mouse click on a panel that hovering already opened keeps it open; keyboard and touch toggle it.
      const mouse = 'pointerType' in event ? /** @type {PointerEvent} */ (event).pointerType === 'mouse' : event.detail > 0;
      if (!mouse) this.close(true);
      return;
    }
    this.open(item);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[slot="more"]')) {
      // Horizon's own on:focus opens "More"; keep it hidden when Escape put the focus here.
      if (!this.#escaping) this.#hideMore(false);
      return;
    }
    const item = target.closest('[data-fd-item]');
    if (!(item instanceof HTMLElement)) return;
    if (item.slot === 'overflow') {
      this.#hideMore(false);
      this.#menu?.activate?.({ target: item });
    } else {
      this.#leaveMore();
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    const item = event.target instanceof Element ? event.target.closest('[data-fd-item]') : null;
    if (item instanceof HTMLElement && item.slot === 'overflow') {
      this.#menu?.deactivate?.({ target: item });
      const list = this.querySelector('overflow-list');
      if (!(next instanceof Node) || !list?.contains(next)) this.#releaseMore();
    }
    if (this.#open && next instanceof Node && !this.#open.contains(next)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#open) {
      this.close(this.#open.contains(document.activeElement));
      return;
    }
    if (!this.#moreItem) return;
    const active = document.activeElement;
    const inMore = active instanceof Element && this.contains(active) && !!active.closest('[slot="overflow"]');
    this.#hideMore(true);
    this.#releaseMore();
    if (inMore) {
      const more = this.querySelector('[slot="more"] > button');
      this.#escaping = true;
      if (more instanceof HTMLElement) more.focus();
      this.#escaping = false;
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.#open && event.target instanceof Node && !this.contains(event.target)) this.close();
  };

  /** Theme editor: selecting a mega menu card block opens its panel. */
  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    const item = target.closest('[data-fd-item]');
    if (item instanceof HTMLElement && item.slot !== 'overflow') this.open(item);
  };

  #onBlockDeselect = (/** @type {Event} */ event) => {
    if (event.target instanceof Element && this.contains(event.target)) this.close();
  };
}

if (!customElements.get('fd-mega-nav')) {
  customElements.define('fd-mega-nav', FdMegaNav);
}

/**
 * <fd-region-drop>: the desktop region & language button and its dropdown (`[data-fd-region-pop]`).
 * Opens on click / Enter / Space, closes on a click outside (including the dimmed page), Escape (focus back on the
 * button), focus leaving it, another opener (`fd:header-open`) or `fd:header-close`. Choosing a row submits the
 * localization form natively, so no submit handling is needed here.
 */
class FdRegionDrop extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.toggleButton?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        const next = event.relatedTarget;
        if (this.isOpen && next instanceof Node && !this.contains(next)) this.close();
      },
      { signal }
    );
    document.addEventListener(
      'click',
      (event) => {
        if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close(true);
      },
      { signal }
    );
    document.addEventListener(CLOSE_EVENT, () => this.close(), { signal });
    document.addEventListener(
      OPEN_EVENT,
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.source !== this) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.close();
  }

  /** @returns {HTMLButtonElement | null} */
  get toggleButton() {
    return this.querySelector('[data-fd-region-toggle]');
  }

  /** @returns {HTMLElement | null} */
  get popup() {
    return this.querySelector('[data-fd-region-pop]');
  }

  get isOpen() {
    const popup = this.popup;
    return !!popup && !popup.hidden;
  }

  open() {
    const popup = this.popup;
    if (!popup || !popup.hidden) return;
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { source: this } }));
    popup.hidden = false;
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    dim.show(this);
  }

  /** @param {boolean} [restoreFocus] */
  close(restoreFocus = false) {
    const popup = this.popup;
    if (!popup || popup.hidden) return;
    popup.hidden = true;
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    dim.hide(this);
    if (restoreFocus) this.toggleButton?.focus();
  }
}

if (!customElements.get('fd-region-drop')) {
  customElements.define('fd-region-drop', FdRegionDrop);
}
